import { finishCodesMatch, normalizeFinishCode } from "./finishCode.js";
import { roundOutlookQty } from "./inventoryOutlookFormat.js";

export const COLOR_CROSSWALK_FIELDS = [
  "width",
  "length",
  "series",
  "roof_style",
  "package",
  "roof_type",
  "roof_color",
  "siding_type",
  "siding_color",
  "trim_color",
  "addon_name",
] as const;

export type ColorCrosswalkField = (typeof COLOR_CROSSWALK_FIELDS)[number];

const FIELD_SET = new Set<string>(COLOR_CROSSWALK_FIELDS);

export type ColorCrosswalkModifier = {
  field: ColorCrosswalkField;
  op: "equals" | "contains" | "one_of";
  value: string;
};

export type ColorCrosswalkRow = {
  id: string;
  source_field: ColorCrosswalkField;
  source_value: string;
  modifiers: ColorCrosswalkModifier[];
  finish_channel: string;
  finish_code: string;
  priority: number;
};

export type ColorCrosswalkAspects = {
  fields: Record<string, string>;
  addons: Array<{ name: string; quantity: number }>;
};

export type ColorCrosswalkMatch =
  | { status: "match"; finish_code: string; row_id: string }
  | { status: "ambiguous" }
  | { status: "none" };

export function parseColorCrosswalkModifiers(raw: unknown): ColorCrosswalkModifier[] | null {
  if (!Array.isArray(raw)) return null;
  const out: ColorCrosswalkModifier[] = [];
  for (const item of raw) {
    if (!item || typeof item !== "object" || Array.isArray(item)) return null;
    const row = item as Record<string, unknown>;
    const field = typeof row.field === "string" ? row.field.trim() : "";
    const op = row.op === "contains" ? "contains" : row.op === "one_of" ? "one_of" : row.op === "equals" ? "equals" : "";
    const value = typeof row.value === "string" ? row.value.trim() : "";
    if (!FIELD_SET.has(field) || !op || !value) return null;
    if (op === "contains" && field !== "addon_name") return null;
    if (op === "one_of" && field === "addon_name") return null;
    out.push({ field: field as ColorCrosswalkField, op, value });
  }
  return out;
}

function addonHits(name: string, modifier: ColorCrosswalkModifier): boolean {
  const left = name.trim().toLowerCase();
  const right = modifier.value.trim().toLowerCase();
  if (!left || !right) return false;
  return modifier.op === "contains" ? left.includes(right) : left === right;
}

function modifierHits(bag: ColorCrosswalkAspects, modifier: ColorCrosswalkModifier): boolean {
  if (modifier.field === "addon_name") return bag.addons.some((addon) => addonHits(addon.name, modifier));
  const actual = normalizeFinishCode(bag.fields[modifier.field] ?? "");
  if (!actual) return false;
  if (modifier.op === "one_of") {
    const options = modifier.value.split(",").map((part) => normalizeFinishCode(part)).filter(Boolean);
    return options.includes(actual);
  }
  if (modifier.op !== "equals") return false;
  return actual === normalizeFinishCode(modifier.value);
}

function sourceHits(bag: ColorCrosswalkAspects, row: ColorCrosswalkRow): boolean {
  if (row.source_field === "addon_name") {
    return bag.addons.some((addon) => finishCodesMatch(addon.name, row.source_value));
  }
  return finishCodesMatch(bag.fields[row.source_field] ?? "", row.source_value);
}

/** Most specific matching row wins. A tie on a different finish code stays unresolved. */
export function matchColorCrosswalk(
  rows: readonly ColorCrosswalkRow[],
  bag: ColorCrosswalkAspects,
  finishChannel: string | null | undefined,
): ColorCrosswalkMatch {
  const channel = normalizeFinishCode(finishChannel);
  if (!channel) return { status: "none" };
  const matching = rows.filter((row) => {
    if (normalizeFinishCode(row.finish_channel) !== channel) return false;
    if (!sourceHits(bag, row)) return false;
    return row.modifiers.every((modifier) => modifierHits(bag, modifier));
  });
  if (matching.length === 0) return { status: "none" };
  const specificity = Math.max(...matching.map((row) => row.modifiers.length));
  let winners = matching.filter((row) => row.modifiers.length === specificity);
  const priority = Math.min(...winners.map((row) => row.priority));
  winners = winners.filter((row) => row.priority === priority);
  const codes = new Set(winners.map((row) => normalizeFinishCode(row.finish_code)));
  if (codes.size !== 1) return { status: "ambiguous" };
  return { status: "match", finish_code: [...codes][0] ?? "", row_id: winners[0]!.id };
}

export type ColorCrosswalkDemand = {
  material_use_id: string;
  use_code: string;
  material_id: string;
  quantity: number;
};

export type ColorCrosswalkGap = ColorCrosswalkDemand & {
  reason: "assumed" | "no_match" | "ambiguous";
};

export type ColorCrosswalkMember = {
  material_id: string;
  finish_code: string | null;
  is_primary: boolean;
  is_active: boolean;
};

function addDemand(demands: ColorCrosswalkDemand[], materialId: string, useId: string, useCode: string, quantity: number) {
  const qty = roundOutlookQty(quantity);
  if (qty <= 0) return;
  const existing = demands.find((row) => row.material_id === materialId);
  if (existing) existing.quantity = roundOutlookQty(existing.quantity + qty);
  else demands.push({ material_id: materialId, material_use_id: useId, use_code: useCode, quantity: qty });
}

function takeDemand(demands: ColorCrosswalkDemand[], materialId: string, quantity: number): number {
  const existing = demands.find((row) => row.material_id === materialId);
  if (!existing) return 0;
  const moved = Math.min(existing.quantity, quantity);
  existing.quantity = roundOutlookQty(existing.quantity - moved);
  return roundOutlookQty(moved);
}

function pickMember(members: readonly ColorCrosswalkMember[], finishCode: string): string | null {
  const hits = members.filter((member) => member.is_active && finishCodesMatch(member.finish_code, finishCode));
  if (hits.length === 0) return null;
  const primary = hits.find((member) => member.is_primary);
  if (primary) return primary.material_id;
  return [...hits].sort((a, b) => a.material_id.localeCompare(b.material_id))[0]?.material_id ?? null;
}

/**
 * Swap a default SKU for the member whose finish code the crosswalk names.
 * A part use only sees rows for its own finish channel.
 */
export function applyColorCrosswalk<T extends ColorCrosswalkGap>(input: {
  demands: readonly ColorCrosswalkDemand[];
  gaps: readonly T[];
  aspects: ColorCrosswalkAspects;
  rows: readonly ColorCrosswalkRow[];
  channelByUse: ReadonlyMap<string, string | null>;
  membersByUse: ReadonlyMap<string, readonly ColorCrosswalkMember[]>;
}): { demands: ColorCrosswalkDemand[]; gaps: T[] } {
  const demands = input.demands.map((row) => ({ ...row }));
  const gaps: T[] = [];
  for (const gap of input.gaps) {
    if (gap.quantity <= 0) continue;
    const matched = matchColorCrosswalk(input.rows, input.aspects, input.channelByUse.get(gap.material_use_id));
    if (matched.status === "none") {
      gaps.push(gap);
      continue;
    }
    if (matched.status === "ambiguous") {
      gaps.push({ ...gap, reason: "ambiguous" });
      continue;
    }
    const materialId = pickMember(input.membersByUse.get(gap.material_use_id) ?? [], matched.finish_code);
    if (!materialId) {
      gaps.push({ ...gap, reason: "no_match" });
      continue;
    }
    if (materialId === gap.material_id) continue;
    const moved = takeDemand(demands, gap.material_id, gap.quantity);
    addDemand(demands, materialId, gap.material_use_id, gap.use_code, moved);
  }
  return { demands: demands.filter((row) => row.quantity > 0), gaps };
}
